import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Mail, Phone, ArrowRight, Globe, Palette, Zap } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const Start = () => {
  return (
    <div data-testid="start-page">
      {/* Hero — single focused CTA */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-2xl mx-auto text-center">
            <p className="text-accent font-medium text-sm uppercase tracking-wider mb-4">
              Thanks for scanning
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Nice to meet you.
            </h1>
            <p className="text-lg text-muted-foreground mb-10 max-w-lg mx-auto">
              We're Dockside Design — we build modern, professional websites
              for local businesses across the UK.
            </p>
            <Button
              asChild
              data-testid="start-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10 py-6 text-lg font-medium shadow-md transition-all hover:scale-105"
            >
              <Link to="/contact">
                Get a Free Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* What we do — brief, scannable */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">Design & Build</h3>
              <p className="text-muted-foreground text-sm">Custom websites tailored to your business and brand.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Palette className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">Hosting & Support</h3>
              <p className="text-muted-foreground text-sm">Reliable hosting with ongoing support included.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">SEO & Performance</h3>
              <p className="text-muted-foreground text-sm">Built fast and optimised to attract customers.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-2xl mx-auto text-center">
            <Card className="border-border/50">
              <CardContent className="p-8 md:p-10">
                <p className="text-lg md:text-xl text-foreground italic mb-6">
                  "No corporate nonsense, no months of waiting — just practical,
                  effective websites that bring customers through your door."
                </p>
                <p className="text-sm text-muted-foreground">
                  Websites from <span className="font-semibold text-foreground">£79/month</span> or <span className="font-semibold text-foreground">£795 one-off</span>
                </p>
                <Link
                  to="/pricing"
                  className="text-accent hover:underline text-sm font-medium mt-2 inline-block"
                >
                  View pricing
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Contact — prominent and easy */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Let's have a chat.
            </h2>
            <p className="text-primary-foreground/80 mb-10">
              Drop us a message or give us a call — no pressure, just a
              friendly conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <a
                href="mailto:welcome@docksidedesign.co.uk"
                className="flex items-center justify-center gap-3 bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors rounded-xl px-6 py-4"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm">welcome@docksidedesign.co.uk</span>
              </a>
              <a
                href="tel:+447799378086"
                className="flex items-center justify-center gap-3 bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors rounded-xl px-6 py-4"
              >
                <Phone className="w-5 h-5" />
                <span className="text-sm">+44 7799 378086</span>
              </a>
            </div>
            <Button
              asChild
              data-testid="start-contact-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10 py-6 text-lg font-medium"
            >
              <Link to="/contact">
                Send Us a Message <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Start;
